import * as S from "./styles";

import { useEffect, useRef, useState } from "react";

import GradeIcon from "@material-ui/icons/Grade";
import { useHistory } from "react-router-dom";
import { useGlobal } from "../../../contexts/globalContext";
import BookingsRepository from "../../../repositories/bookingsRepository";
import PackageRepository from "../../../repositories/packageRepository";
import Auth2faModal from "../../Auth2FaModal";
import Loader from "../../Loader";
import ReprocessarPagamento from "../../ReprocessarPagamento";

import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import { safeFormat } from "../../../utils/dateHelpers";

const PackageTourCard = ({ booking = [], coinAlias }) => {
  let history = useHistory();

  const bookingsRepository = new BookingsRepository();
  const packageRepository = new PackageRepository();
  const { showAlert } = useGlobal();

  const [openDialogReprocessar, setOpenDialogReprocessar] = useState(false);
  const [openLoader, setOpenLoader] = useState(false);
  const [openModal2fa, setOpenModal2fa] = useState(false);
  const [packageDetails, setPackageDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  const closeModal2fa = () => setOpenModal2fa(false);

  const onChangeOpenDialogReprocessar = (open) =>
    setOpenDialogReprocessar(open);

  // Lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchPackageDetails = async () => {
      if (!isVisible || !booking.locator) return;

      const cacheKey = `package_details_${booking.locator}`;
      const cached = sessionStorage.getItem(cacheKey);

      if (cached) {
        try {
          setPackageDetails(JSON.parse(cached));
          return;
        } catch (e) {
          sessionStorage.removeItem(cacheKey);
        }
      }

      setLoadingDetails(true);
      try {
        const response = await packageRepository.getPackageDetails(
          booking.locator
        );

        if (response.status === 200 && response.data?.success) {
          const data = response.data.data;
          setPackageDetails(data);

          sessionStorage.setItem(cacheKey, JSON.stringify(data));
        }
      } catch (error) {
        console.error("Error fetching package details:", error);
      } finally {
        setLoadingDetails(false);
      }
    };

    fetchPackageDetails();
  }, [isVisible, booking.locator]);

  const getStatusTranslate = (status) => {
    switch (status) {
      case 1:
        return "pendente";
      case 2:
        return "emitido";
      case 3:
        return "expirado";
      case 4:
        return "finalizado";
      case 5:
        return "cancelado";
      default:
        break;
    }
  };

  const reprocessar = () => {
    if (
      (booking.paymentTransaction === null ||
        booking.paymentTransaction === undefined) &&
      booking.cashTotalValue > 0
    ) {
      onChangeOpenDialogReprocessar(true);
    } else {
      setOpenLoader(true);
      bookingsRepository
        .reprocessarPagamento(booking.bookingCode, {})
        .then((res) => {
          setOpenLoader(false);
          sessionStorage.setItem("@bookingCompleted", JSON.stringify(res));
          switch (res.status) {
            case 200:
              history.push("/busca/finalizado");
              break;
            case 201:
              if (res.data.auth2fa) {
                if (res.data.redirectAuth2fa !== null) {
                  setOpenLoader(true);
                  window.location.replace(res.data.redirectAuth2fa);
                } else {
                  setOpenModal2fa(true);
                }
              } else {
                history.push("/busca/finalizado");
              }
              break;
            default:
              break;
          }
        })
        .catch((err) => {
          setOpenLoader(false);
          switch (err?.response?.status) {
            case 400:
              showAlert("Há algo de errado nos seus dados, verifique!");
              break;
            case 401:
              history.push("/session-expires");
              break;
            case 409:
              sessionStorage.setItem(
                "@bookingError",
                JSON.stringify(err.response.data.error)
              );
              sessionStorage.setItem(
                "reprocessar",
                JSON.stringify({ reprocessar: true })
              );
              history.push("/busca/booking-error");
              break;
            case 504:
              sessionStorage.setItem(
                "@bookingError",
                JSON.stringify("Tempo expirou, tente novamente!")
              );
              sessionStorage.setItem(
                "reprocessar",
                JSON.stringify({ reprocessar: true })
              );
              history.push("/busca/booking-error");
              break;
            default:
              showAlert(
                "error",
                `Compra não processada, por favor, tente novamente!`
              );
              break;
          }
        });
    }
  };

  const reenviarVoucher = (locator) => {
    bookingsRepository
      .reenviarVoucher(locator)
      .then((res) =>
        showAlert("success", "Voucher reenviado, verifique seu e-mail!")
      )
      .catch((err) => showAlert("error", "Houve algum erro, tente novamente!"));
  };

  const bookingPackage = packageDetails?.bookingPackages?.[0];
  const packageInfo =
    bookingPackage?.package || booking?.packageAvails?.[0]?.package || {};

  // Hotel data
  const bookingHotel = bookingPackage?.bookingHotels?.[0];
  const hotel = bookingHotel?.hotel;
  const hotelRoom = bookingHotel?.rooms?.[0];
  const checkIn = hotelRoom?.checkIn;
  const checkOut = hotelRoom?.checkOut;

  // Flight data
  const bookingFlight = bookingPackage?.bookingFlights?.[0];
  const flights = bookingFlight?.flights || [];
  const firstFlight = flights[0];
  const secondFlight = flights[1];

  // Transfer data
  const bookingTransfer = bookingPackage?.bookingTransfers?.[0];

  // Passengers from hotel room or flight
  const passengers =
    hotelRoom?.names || bookingFlight?.names || booking.passengers || [];

  // Locators
  const hotelLocator = bookingHotel?.locators?.find(
    (l) => l.type === "SUPPLIER"
  )?.code;
  const flightLocator = bookingFlight?.locators?.find(
    (l) => l.type === "SUPPLIER"
  )?.code;

  // Cancellation policies
  const hotelCancellationPolicy = hotelRoom?.cancellationPolicies;

  const calculateNights = () => {
    if (checkIn && checkOut) {
      const start = new Date(checkIn);
      const end = new Date(checkOut);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
    return packageInfo.nights || 3;
  };
  const nights = calculateNights();
  const days = nights + 1;

  const starsRatingInteger = Math.round(hotel?.stars || 0);
  const stars = Array(isNaN(starsRatingInteger) ? 0 : starsRatingInteger)
    .fill()
    .map((_, idx) => idx);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      return safeFormat(dateString, "dd/MM/yyyy", { locale: pt });
    } catch (error) {
      return dateString;
    }
  };

  const formatCancellationPolicy = (policy) => {
    if (!policy) return "";

    if (typeof policy === "string") return policy;

    if (typeof policy === "object") {
      if (policy.refundable === false) {
        return "Não reembolsável";
      } else if (policy.refundable === true) {
        const parts = ["Reembolsável"];
        if (policy.penalties && policy.penalties.length > 0) {
          parts.push(" - Consulte as penalidades");
        }
        return parts.join("");
      }
    }

    return "";
  };

  return (
    <S.BlankCard ref={cardRef}>
      <ReprocessarPagamento
        open={openDialogReprocessar}
        onClose={onChangeOpenDialogReprocessar}
        booking={booking}
      />
      <Loader openLoader={openLoader} />
      <Auth2faModal
        open={openModal2fa}
        onClose={closeModal2fa}
        booking={{ data: booking }}
        type="reprocessar"
      />
      <S.CardHeader>
        <S.Title>
          Código da reserva: {booking?.bookingCode}
          <span className="subtitle">
            Data da reserva:{" "}
            {booking?.bookingDateStr !== undefined &&
              booking?.bookingDateStr !== "Invalid Date" &&
              safeFormat(booking?.bookingDateStr, "dd/MM/yyyy", {
                locale: pt,
              })}
          </span>
        </S.Title>
        {booking?.statusId && (
          <S.StatusContainer>
            <S.Status
              className={`status ${getStatusTranslate(booking.statusId)}`}
            >
              {getStatusTranslate(booking.statusId)}
            </S.Status>
          </S.StatusContainer>
        )}
      </S.CardHeader>

      <S.Subtitle>Detalhes do Pacote</S.Subtitle>

      {loadingDetails && (
        <S.LoadingMessage>Carregando detalhes...</S.LoadingMessage>
      )}

      <S.PackageSummaryCardDetails>
        <div>
          {hotel?.images && hotel.images.length > 0 ? (
            <img
              src={
                hotel.images[0].medium ||
                hotel.images[0].large ||
                hotel.images[0].small
              }
              alt={hotel?.name || "Hotel"}
            />
          ) : (
            <S.ImagePlaceholder>
              <span>Imagem não disponibilizada pelo fornecedor</span>
            </S.ImagePlaceholder>
          )}
        </div>
        <S.PackageDetails>
          <div className="content">
            {booking.description && (
              <div className="package-description">{booking.description}</div>
            )}
            {hotel && (
              <>
                <div className="stars">
                  {stars.map((star, index) => (
                    <GradeIcon fontSize="small" key={index} />
                  ))}
                </div>
                <div className="hotel-details">
                  <span className="hotel-name">{hotel.name}</span>
                </div>
                {(checkIn || checkOut) && (
                  <div className="dates-info">
                    Check-in: {formatDate(checkIn)} | Check-out:{" "}
                    {formatDate(checkOut)}
                    {nights > 0 && ` (${nights} noites)`}
                  </div>
                )}
                {hotelCancellationPolicy &&
                  formatCancellationPolicy(hotelCancellationPolicy) && (
                    <div className="cancel-policy">
                      <b>Política de cancelamento:</b>{" "}
                      {formatCancellationPolicy(hotelCancellationPolicy)}
                    </div>
                  )}
              </>
            )}
          </div>
          <div className="package-info">
            {(firstFlight || secondFlight) && (
              <div className="flights">
                <b>Voos:</b>
                {firstFlight && (
                  <span>
                    {firstFlight.origin?.code} → {firstFlight.destination?.code}{" "}
                    ({formatDate(firstFlight.departure)})
                  </span>
                )}
                {secondFlight && (
                  <span>
                    {secondFlight.origin?.code} →{" "}
                    {secondFlight.destination?.code} (
                    {formatDate(secondFlight.departure)})
                  </span>
                )}
              </div>
            )}
            {bookingTransfer && (
              <div className="transfer">
                <b>Transfer:</b> Incluído
              </div>
            )}
            {(hotelLocator || flightLocator) && (
              <div className="locators">
                {hotelLocator && (
                  <span>
                    <b>Locator Hotel:</b> {hotelLocator}
                  </span>
                )}
                {flightLocator && (
                  <span>
                    <b>Locator Voo:</b> {flightLocator}
                  </span>
                )}
              </div>
            )}
          </div>
        </S.PackageDetails>
      </S.PackageSummaryCardDetails>

      <S.BottomInfos>
        {passengers && passengers.length > 0 && (
          <S.Travellers>
            <S.Subtitle>VIAJANTES</S.Subtitle>
            {passengers.map((passenger, idx) => (
              <S.TravellerName key={idx}>
                {passenger.name ||
                  `${passenger.firstName || ""} ${
                    passenger.lastName || ""
                  }`.trim()}
              </S.TravellerName>
            ))}
          </S.Travellers>
        )}
        {booking.pointsTotalValue ? (
          <S.ValorTotal>
            Valor em {coinAlias}: {booking.pointsTotalValue}
          </S.ValorTotal>
        ) : (
          ""
        )}
        {booking.cashTotalValue ? (
          <S.ValorTotal>
            {" "}
            Valor em R$: {booking.cashTotalValue.toLocaleString("pt-BR")}
          </S.ValorTotal>
        ) : (
          ""
        )}
      </S.BottomInfos>
      {booking.bookingExpirationDate && (
        <S.ExpirationDate>
          <span>
            Esta reserva expira em:{" "}
            {booking?.bookingExpirationDate !== undefined &&
              booking?.bookingExpirationDate !== "Invalid Date" &&
              format(
                parseISO(booking?.bookingExpirationDate),
                "dd/MM/yyyy HH:mm",
                {
                  locale: pt,
                }
              )}
          </span>
        </S.ExpirationDate>
      )}
    </S.BlankCard>
  );
};

export default PackageTourCard;
